import { useEffect, useRef, useState } from "react";
import { Magnet, Plus, UploadSimple, X, SpinnerGap, Truck } from "@phosphor-icons/react";
import { Reveal } from "../components/Reveal";
import { api, API } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useSiteImages } from "../context/SiteImagesContext";
import { toast } from "sonner";

const toBase64 = (file) =>
  new Promise((resolveB64, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolveB64(String(reader.result).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function Imanes() {
  const { addItem } = useCart();
  const { resolve } = useSiteImages();
  const [pricing, setPricing] = useState(null);
  const [customFiles, setCustomFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  if (!pricing?.magnets) return null;

  const { unit_price: unit, pack4_price: pack4, size, max_custom, designs } = pricing.magnets;
  const shipping = pricing?.shipping?.price_magnet_es;
  const nCustom = customFiles.length;
  const customTotal = Math.floor(nCustom / 4) * pack4 + (nCustom % 4) * unit;

  const addSolidario = (design, qty) => {
    const price = qty === 4 ? pack4 : unit;
    addItem({
      key: `magnet__${design.id}__${qty}`,
      kind: "magnet",
      magnet_design: design.id,
      upload_ids: [],
      magnet_qty: qty,
      model_id: null,
      model_name: "Imán solidario VITAE",
      size: null,
      size_label: `${design.label} · ${size} · ${qty === 4 ? "4 unidades (oferta)" : "1 unidad"}`,
      finish: null,
      finish_label: "",
      specialties: [],
      name_personalization: null,
      price,
      image: resolve(`iman:${design.id}`, "") || null,
      pen_hex: "#EBD9D2",
      slug: `iman-${design.id}-${qty}`,
    });
    toast.success(`Imán ${design.label} añadido al carrito`);
  };

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((f) => f.type.startsWith("image/") && f.size <= 6 * 1024 * 1024);
    if (valid.length !== files.length) {
      toast.error("Solo imágenes de hasta 6 MB");
    }
    setCustomFiles((prev) => {
      const room = Math.max(0, max_custom - prev.length);
      const next = valid.slice(0, room).map((f) => ({ file: f, preview: URL.createObjectURL(f) }));
      if (valid.length > room) toast.error(`Máximo ${max_custom} imanes personalizados por pedido`);
      return [...prev, ...next];
    });
    e.target.value = "";
  };

  const removeCustom = (idx) => {
    setCustomFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const addPersonalizado = async () => {
    if (!nCustom || uploading) return;
    setUploading(true);
    try {
      const ids = [];
      for (const f of customFiles) {
        const base64 = await toBase64(f.file);
        const { data } = await api.post("/uploads", { content_type: f.file.type, data: base64 });
        ids.push(data.id);
      }
      addItem({
        key: `magnet__pers__${Date.now()}`,
        kind: "magnet",
        magnet_design: "personalizado",
        upload_ids: ids,
        magnet_qty: ids.length,
        model_id: null,
        model_name: "Imán personalizado VITAE",
        size: null,
        size_label: `${size} · ${ids.length} ${ids.length === 1 ? "unidad" : "unidades"} · con tus fotos`,
        finish: null,
        finish_label: "",
        specialties: [],
        name_personalization: null,
        price: customTotal,
        image: `${API}/uploads/${ids[0]}`,
        slug: "iman-personalizado",
      });
      setCustomFiles([]);
      toast.success("Imanes personalizados añadidos al carrito");
    } catch {
      toast.error("No se pudieron subir las imágenes, inténtalo de nuevo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <section id="imanes" data-testid="magnets-section" className="py-24 md:py-32 bg-[#F6F1E7]">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-2xl">
          <p className="overline mb-6">
            <span className="text-[#D7C6A3]">08</span> &nbsp;/&nbsp; Imanes VITAE
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D3732] leading-tight">
            Imanes solidarios, y también con tus propias fotos.
          </h2>
          <p className="text-[#6B705C] mt-5 font-light leading-relaxed">
            Imanes de {size} con diseños solidarios por las enfermedades que nos tocan de cerca.{" "}
            <strong className="font-normal text-[#2D3732]">{unit.toFixed(2)} € la unidad</strong> o{" "}
            <strong className="font-normal text-[#2D3732]">{pack4.toFixed(2)} € el pack de 4</strong>.
            {shipping ? ` Envío ${shipping.toFixed(2)} €.` : ""}
          </p>
        </Reveal>

        {/* Solidarity designs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mt-12">
          {designs.map((design, i) => {
            const img = resolve(`iman:${design.id}`, "");
            return (
              <Reveal key={design.id} delay={0.03 * (i % 4)}>
                <div
                  data-testid={`magnet-card-${design.id}`}
                  className="bg-white border border-[#EBEBEB] rounded-3xl overflow-hidden h-full flex flex-col"
                >
                  <div className="h-32 md:h-36 flex items-center justify-center bg-gradient-to-br from-[#EBD9D2]/60 to-[#EEF1E8]/60">
                    {img ? (
                      <img src={img} alt={`Imán solidario ${design.label}`} className="w-full h-full object-cover" />
                    ) : (
                      <Magnet size={40} weight="thin" className="text-[#C8A2A8]" />
                    )}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="text-sm text-[#2D3732] font-medium leading-snug flex-1">
                      {design.label}
                    </h3>
                    <p className="text-[0.65rem] text-[#9CA3AF] mt-0.5">{size}</p>
                    <div className="grid grid-cols-2 gap-1.5 mt-3">
                      <button
                        data-testid={`magnet-add-${design.id}-1`}
                        onClick={() => addSolidario(design, 1)}
                        className="border border-[#A3B18A] text-[#2D3732] hover:bg-[#EEF1E8] rounded-full py-2 text-xs transition-colors duration-300 flex items-center justify-center gap-1"
                      >
                        <Plus size={12} /> 1 ud · {unit.toFixed(2)} €
                      </button>
                      <button
                        data-testid={`magnet-add-${design.id}-4`}
                        onClick={() => addSolidario(design, 4)}
                        className="bg-[#A3B18A] hover:bg-[#8A9A6F] text-white rounded-full py-2 text-xs transition-colors duration-300 flex items-center justify-center gap-1"
                      >
                        <Plus size={12} /> 4 uds · {pack4.toFixed(2)} €
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Personalised magnets */}
        <Reveal delay={0.1}>
          <div
            data-testid="magnet-custom-block"
            className="mt-12 bg-white border border-[#EBEBEB] rounded-3xl p-8 md:p-10"
          >
            <div className="max-w-2xl">
              <p className="overline mb-3">Con tus propias imágenes</p>
              <h3 className="font-display text-3xl text-[#2D3732] leading-tight">
                Imán personalizado
              </h3>
              <p className="text-[#6B705C] mt-3 font-light leading-relaxed text-sm">
                Sube las fotos que quieras convertir en imán ({size}). Cada imagen es un imán —
                mismo precio: {unit.toFixed(2)} € la unidad, {pack4.toFixed(2)} € cada 4.
              </p>
            </div>

            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              data-testid="magnet-upload-input"
              onChange={onPickFiles}
            />

            <div className="flex flex-wrap items-center gap-4 mt-6">
              {customFiles.map((f, i) => (
                <div key={i} data-testid={`magnet-preview-${i}`} className="relative w-20 h-20 rounded-xl overflow-hidden border border-[#EBEBEB]">
                  <img src={f.preview} alt={`Imagen ${i + 1}`} className="w-full h-full object-cover" />
                  <button
                    data-testid={`magnet-remove-${i}`}
                    onClick={() => removeCustom(i)}
                    className="absolute top-1 right-1 w-5 h-5 bg-white/90 rounded-full flex items-center justify-center"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
              {nCustom < max_custom && (
                <button
                  data-testid="magnet-upload-btn"
                  onClick={() => fileRef.current?.click()}
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-[#A3B18A] text-[#8A9A6F] hover:bg-[#EEF1E8] transition-colors flex flex-col items-center justify-center gap-1"
                >
                  <UploadSimple size={20} weight="light" />
                  <span className="text-[0.6rem]">Subir foto</span>
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-6">
              <button
                data-testid="magnet-add-custom"
                onClick={addPersonalizado}
                disabled={!nCustom || uploading}
                className="bg-[#2D3732] hover:bg-[#1f2622] text-white rounded-full px-6 py-3 text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {uploading && <SpinnerGap size={16} className="animate-spin" />}
                {nCustom
                  ? `Añadir ${nCustom} ${nCustom === 1 ? "imán" : "imanes"} · ${customTotal.toFixed(2)} €`
                  : "Sube al menos una foto"}
              </button>
              {nCustom > 0 && nCustom % 4 !== 0 && (
                <p className="text-xs text-[#9CA3AF]">
                  Con {nCustom + (4 - (nCustom % 4))} te sale el pack a {pack4.toFixed(2)} € cada 4.
                </p>
              )}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="text-xs text-[#9CA3AF] mt-6 flex items-center gap-2">
            <Truck size={15} weight="light" />
            Los pedidos de solo imanes viajan en sobre: {pricing?.shipping?.price_magnet_es?.toFixed(2)} € España · {pricing?.shipping?.price_magnet_eu?.toFixed(2)} € Europa · {pricing?.shipping?.price_magnet_am?.toFixed(2)} € América · {pricing?.shipping?.price_magnet_row?.toFixed(2)} € resto del mundo.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
